import React, { useState, useEffect } from "react";
import { HiOutlineChevronDown, HiOutlineChevronUp } from "react-icons/hi";
import { FaPlus, FaTrash } from "react-icons/fa";
import Highlighter from "react-highlight-words";
import useSearchPagination from "../../../../../ReusableComponents/SearchPagination/useSearchPagination";
import LoadingWithLable from "../../../../../ReusableComponents/Loading/LoadingWithLable";
import nodeinstance from "../../../../../nodeInstance";

export default function Statement({ data, isLocalExit, employeeID, onTotalChange, onSalaryModeChange }) {
  const defaultTitles = [
    "SEQUENCE",
    "TOTAL WORKING DAYS",
    "MONTHS",
    "BASIC SALARY",
    "TOTAL SALARY",
    "V.PAY",
    "EOSB",
    "TOTAL",
  ];

  // State to manage collapsible section visibility
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Salary type toggle: true = Basic Salary, false = Total Salary
  // Initialize from saved value or default to true
  const [useBasicSalary, setUseBasicSalary] = useState(
    data?.salaryCalculationMode !== undefined
      ? data.salaryCalculationMode
      : true
  );

  console.log("Statement - data:", data);
  console.log("Statement - salaryCalculationMode from data:", data?.salaryCalculationMode);
  console.log("Statement - Current useBasicSalary state:", useBasicSalary);

  // Update state when data changes
  useEffect(() => {
    console.log("Statement useEffect - salaryCalculationMode:", data?.salaryCalculationMode);
    if (data?.salaryCalculationMode !== undefined) {
      console.log("Statement - Updating useBasicSalary to:", data.salaryCalculationMode);
      setUseBasicSalary(data.salaryCalculationMode);
    }
  }, [data?.salaryCalculationMode]);

  // Notify parent when salary calculation mode changes
  useEffect(() => {
    if (onSalaryModeChange) {
      onSalaryModeChange(useBasicSalary);
    }
  }, [useBasicSalary, onSalaryModeChange]);

  // State for custom columns
  const [customColumns, setCustomColumns] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [newColumnName, setNewColumnName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Function to toggle the collapsible section
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Fetch custom columns and values on component mount
  useEffect(() => {
    const fetchCustomData = async () => {
      if (!employeeID) return;

      const endpoint = isLocalExit ? "localexit" : "mobilization";
      const token = localStorage.getItem("nodeToken");

      try {
        // Fetch custom columns
        const columnsResponse = await nodeinstance.get(
          `/${endpoint}/fandf-custom-columns/${employeeID}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (columnsResponse.status === 200) {
          setCustomColumns(columnsResponse.data.data || []);
        }

        // Fetch custom values
        const valuesResponse = await nodeinstance.get(
          `/${endpoint}/fandf-custom-values/${employeeID}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (valuesResponse.status === 200) {
          const valuesData = valuesResponse.data;
          // Transform values into a map: { column_id: { journey_id: value } }
          const valuesMap = {};
          valuesData.data.forEach((valueItem) => {
            const columnId = valueItem.custom_column_id;
            const journeyId = valueItem.journey_id;
            if (!valuesMap[columnId]) {
              valuesMap[columnId] = {};
            }
            valuesMap[columnId][journeyId] = valueItem.value;
          });
          setCustomValues(valuesMap);
        }
      } catch (error) {
        console.error("Error fetching custom data:", error);
      }
    };

    fetchCustomData();
  }, [employeeID, isLocalExit]);

  // Add a new custom column
  const handleAddColumn = () => {
    if (!newColumnName.trim()) return;

    const newColumn = {
      id: Date.now(), // Temporary ID until saved
      column_name: newColumnName.trim(),
      column_order: customColumns.length,
    };

    setCustomColumns([...customColumns, newColumn]);
    setNewColumnName("");
    setIsAddingColumn(false);
  };

  // Delete a custom column
  const handleDeleteColumn = (columnId) => {
    setCustomColumns(customColumns.filter((col) => col.id !== columnId));
    const newValues = { ...customValues };
    delete newValues[columnId];
    setCustomValues(newValues);
  };

  // Update custom value for a specific journey and column
  const handleValueChange = (columnId, journeyId, value) => {
    setCustomValues((prev) => ({
      ...prev,
      [columnId]: {
        ...(prev[columnId] || {}),
        [journeyId]: value,
      },
    }));
  };

  // Save custom columns and values
  const handleSaveCustomData = async () => {
    if (!employeeID) return;

    setIsSaving(true);
    const endpoint = isLocalExit ? "localexit" : "mobilization";
    const token = localStorage.getItem("nodeToken");

    try {
      // Prepare data for saving
      const customColumnsData = customColumns.map((column, index) => ({
        column_name: column.column_name,
        values: data?.contractHistory?.map((contract) => ({
          journey_id: contract.journey_id,
          value: customValues[column.id]?.[contract.journey_id] || "",
        })) || [],
      }));

      const response = await nodeinstance.post(
        `/${endpoint}/fandf-custom-data`,
        {
          employee_id: employeeID,
          customColumns: customColumnsData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        alert("Custom columns saved successfully!");
        // Reload custom data to get actual IDs from database
        window.location.reload();
      } else {
        alert(`Failed to save: ${response.data.message}`);
      }
    } catch (error) {
      console.error("Error saving custom data:", error);
      alert("Failed to save custom columns");
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to get basic salary with fallback
  function getBasicSalary(contractSalary) {
    const parsed = parseInt(contractSalary);
    if (isNaN(parsed) || parsed === 0) {
      // Fallback to employee's basic_salary from data
      const fallbackSalary = parseInt(data?.data?.[0]?.basic_salary);
      return isNaN(fallbackSalary) ? 0 : fallbackSalary;
    }
    return parsed;
  }

  // Calculate total salary from contract history item or fallback to employee data
  function getTotalSalary(listItem) {
    const basic = parseInt(listItem?.basicsalary || 0) || parseInt(data?.data?.[0]?.basic || 0);
    const food = parseInt(listItem?.food || 0) || parseInt(data?.data?.[0]?.food || 0);
    const hra = parseInt(listItem?.hra || 0) || parseInt(data?.data?.[0]?.hra || 0);
    const travel = parseInt(listItem?.travel_allowance || 0) || parseInt(data?.data?.[0]?.travel_allowance || 0);
    const mobile = parseInt(listItem?.mobile_allowance || 0) || parseInt(data?.data?.[0]?.mobile_allowance || 0);
    const airTicket = parseInt(listItem?.air_ticket_allowance || 0) || parseInt(data?.data?.[0]?.air_ticket_allowance || 0);
    const other = parseInt(listItem?.other_allowance || 0) || parseInt(data?.data?.[0]?.other_allowance || 0);

    return basic + food + hra + travel + mobile + airTicket + other;
  }

  // Get salary for calculation based on toggle
  function getSalaryForCalculation(listItem) {
    const salary = useBasicSalary
      ? getBasicSalary(listItem?.basicsalary)
      : getTotalSalary(listItem);

    console.log('💰 getSalaryForCalculation:', {
      useBasicSalary,
      basic: getBasicSalary(listItem?.basicsalary),
      total: getTotalSalary(listItem),
      selected: salary
    });

    return salary;
  }

  function getDaysBetweenDates(date1, date2) {
    const firstDate = date1 ? new Date(date1) : new Date();
    const secondDate = date2 ? new Date(date2) : new Date();

    // Calculate the difference in milliseconds
    const differenceInMilliseconds = secondDate - firstDate;

    // Convert milliseconds to days
    // const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);
    const differenceInDays =
      Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));

    return differenceInDays;
  }

  // Calculate F&F Settlement Total and notify parent component
  useEffect(() => {
    console.log("Statement useEffect triggered", {
      hasData: !!data,
      hasContractHistory: !!data?.contractHistory,
      contractHistoryLength: data?.contractHistory?.length,
      hasOnTotalChange: !!onTotalChange,
      customColumnsCount: customColumns.length
    });

    if (!data?.contractHistory) {
      console.log("No contract history, skipping total calculation");
      return;
    }

    const vPayTotal = Math.round(
      data.contractHistory.reduce(
        (total, listItem) =>
          total +
          ((((getSalaryForCalculation(listItem) / 30) * 21) / 12) *
            getDaysBetweenDates(
              listItem?.employee_contract_end__date,
              listItem?.employee_contract_start__date
            )) /
            30.41,
        0
      )
    ) || 0;

    const eosbTotal = Math.round(
      data.contractHistory.reduce(
        (total, listItem) =>
          total +
          ((((getSalaryForCalculation(listItem) / 30) * 15) / 12) *
            getDaysBetweenDates(
              listItem?.employee_contract_end__date,
              listItem?.employee_contract_start__date
            )) /
            30.41,
        0
      )
    ) || 0;

    const customColumnsTotal = customColumns.reduce((sum, column) => {
      const columnTotal = data.contractHistory.reduce((total, listItem) => {
        const value = parseFloat(customValues[column.id]?.[listItem.journey_id] || 0);
        return total + (isNaN(value) ? 0 : value);
      }, 0) || 0;
      return sum + (columnTotal || 0);
    }, 0) || 0;

    const grandTotal = vPayTotal + eosbTotal + customColumnsTotal;

    console.log("F&F Settlement Total calculated:", {
      vPayTotal,
      eosbTotal,
      customColumnsTotal,
      grandTotal,
      hasCallback: !!onTotalChange
    });

    if (onTotalChange) {
      console.log("Calling onTotalChange with grandTotal:", grandTotal);
      onTotalChange(grandTotal);
    } else {
      console.warn("onTotalChange callback not provided!");
    }
  }, [data?.contractHistory, customColumns, customValues, useBasicSalary]);
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 transition-all duration-200 group"
        onClick={toggleCollapse}
        aria-expanded={!isCollapsed}
        aria-controls="collapseWithScrollbar"
      >
        <div className="flex items-center space-x-3">
          <div className={`transform transition-transform duration-200 ${isCollapsed ? "" : "rotate-180"}`}>
            <HiOutlineChevronDown className="w-5 h-5 text-gray-600" />
          </div>
          <span className="text-sm font-bold text-gray-800 uppercase tracking-wide">
            F&F SETTLEMENT
          </span>
        </div>
        <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">
          {isCollapsed ? "Click to Expand" : "Click to Collapse"}
        </span>
      </button>
      <div
        className={`transition-all duration-300 ease-in-out ${
          isCollapsed ? "max-h-0 opacity-0" : "max-h-[5000px] opacity-100"
        } overflow-hidden`}
        id="collapseWithScrollbar"
      >
        <div className="p-6 bg-white">
        <>
          {/* Salary Type Radio Buttons */}
          <div className="mb-4 flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <span className="text-sm font-semibold text-gray-700 uppercase">Calculate V.PAY & EOSB Based On:</span>

            {/* Radio Buttons */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="salaryCalculation"
                  checked={useBasicSalary}
                  onChange={() => setUseBasicSalary(true)}
                  className="w-4 h-4 accent-red-600 bg-gray-100 border-gray-300 focus:ring-0 focus:outline-none"
                  style={{ accentColor: '#dc2626' }}
                />
                <span className={`text-xs font-medium uppercase ${useBasicSalary ? 'text-gray-900 font-bold' : 'text-gray-600'}`}>
                  Basic Salary
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="salaryCalculation"
                  checked={!useBasicSalary}
                  onChange={() => setUseBasicSalary(false)}
                  className="w-4 h-4 accent-red-600 bg-gray-100 border-gray-300 focus:ring-0 focus:outline-none"
                  style={{ accentColor: '#dc2626' }}
                />
                <span className={`text-xs font-medium uppercase ${!useBasicSalary ? 'text-gray-900 font-bold' : 'text-gray-600'}`}>
                  Total Salary
                </span>
              </label>
            </div>

            {/* Info Icon with Tooltip */}
            <div className="relative group">
              <button
                type="button"
                className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-all"
                title="Toggle Information"
              >
                <span className="text-sm font-bold">?</span>
              </button>
              <div className="absolute left-0 top-8 w-96 p-4 bg-white border-2 border-blue-500 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="mb-3">
                  <p className="text-sm font-bold text-gray-900 mb-2">💰 Salary Calculation Mode:</p>
                </div>
                <div className="space-y-3 text-xs text-gray-700">
                  <div className="p-2 bg-gray-50 rounded">
                    <p className="font-semibold text-gray-900 mb-1">🔹 Basic Salary (Default):</p>
                    <p>Uses <span className="font-bold text-blue-600">only Basic Salary</span> for V.PAY & EOSB calculations.</p>
                    <p className="mt-1 text-gray-600 italic">Formula: (Basic Salary ÷ 30 × Days) ÷ 30.41</p>
                  </div>
                  <div className="p-2 bg-blue-50 rounded">
                    <p className="font-semibold text-gray-900 mb-1">🔹 Total Salary:</p>
                    <p>Uses <span className="font-bold text-red-600">Total Salary</span> (Basic + All Allowances) for calculations.</p>
                    <p className="mt-1 text-gray-600">Includes: Basic + Food + HRA + Travel + Mobile + Air Ticket + Other</p>
                    <p className="mt-1 text-gray-600 italic">Formula: (Total Salary ÷ 30 × Days) ÷ 30.41</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-600">
                    <span className="font-semibold">Affects:</span> V.PAY (21 days) & EOSB (15 days) calculations
                  </p>
                </div>
              </div>
            </div>

            <span className="text-xs text-gray-500 italic ml-auto">
              {useBasicSalary ? '(Basic Only)' : '(Basic + Allowances)'}
            </span>
          </div>

          {/* Custom Columns Management UI */}
          <div className="mb-4 p-4 bg-gray-50 rounded">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">
                Custom Columns
              </h3>
              <button
                onClick={() => setIsAddingColumn(!isAddingColumn)}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-btn-red text-white rounded hover:bg-btn-red-hover transition-all duration-150"
              >
                <FaPlus /> BENEFIT PAYABLES
              </button>
            </div>

            {/* Add New Column Form */}
            {isAddingColumn && (
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="text"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  placeholder="Enter column name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === "Enter" && handleAddColumn()}
                />
                <button
                  onClick={handleAddColumn}
                  className="px-4 py-2 bg-btn-red text-white rounded hover:bg-btn-red-hover transition-all duration-150"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setIsAddingColumn(false);
                    setNewColumnName("");
                  }}
                  className="px-4 py-2 bg-btn-dark text-white rounded hover:bg-btn-dark-hover transition-all duration-150"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Custom Columns List */}
            {customColumns.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {customColumns.map((column) => (
                  <div
                    key={column.id}
                    className="flex items-center gap-2 px-3 py-1 bg-white border border-gray-300 rounded"
                  >
                    <span className="text-sm">{column.column_name}</span>
                    <button
                      onClick={() => handleDeleteColumn(column.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FaTrash size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            className="overflow-x-auto"
            style={{ backgroundColor: "white", width: "100%" }}
          >
            <table className="w-full divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  {/* Default columns up to EOSB (before TOTAL) - now includes TOTAL SALARY */}
                  {defaultTitles.slice(0, 7).map((columnName, index) => (
                    <th
                      key={index}
                      scope="col"
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-semibold"
                    >
                      {columnName}
                    </th>
                  ))}
                  {/* Custom Column Headers - BEFORE TOTAL */}
                  {customColumns.map((column) => (
                    <th
                      key={column.id}
                      scope="col"
                      className="px-6 py-4 text-left text-xs font-medium text-blue-600 uppercase tracking-wider font-semibold"
                    >
                      {column.column_name}
                    </th>
                  ))}
                  {/* TOTAL column at the end */}
                  <th
                    scope="col"
                    className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-semibold"
                  >
                    {defaultTitles[7]}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-gray-10" key={`tbody-${useBasicSalary}`}>
                {data?.contractHistory?.map((listItem, index) => {
                  // Use the toggle to determine which salary to use
                  const salaryForCalc = getSalaryForCalculation(listItem);

                  const vPay = Math.round(
                    ((((salaryForCalc / 30) * 21) / 12) *
                      getDaysBetweenDates(
                        listItem?.employee_contract_start__date,
                        listItem?.employee_contract_end__date
                      )) /
                      30.41
                  ) || 0;
                  const eosb = Math.round(
                    ((((salaryForCalc / 30) * 15) / 12) *
                      getDaysBetweenDates(
                        listItem?.employee_contract_start__date,
                        listItem?.employee_contract_end__date
                      )) /
                      30.41
                  ) || 0;

                  // Sum all custom column values for this row
                  const customColumnsSum = customColumns.reduce((sum, column) => {
                    const value = parseFloat(customValues[column.id]?.[listItem.journey_id] || 0);
                    return sum + (isNaN(value) ? 0 : value);
                  }, 0) || 0;

                  const rowTotal = (vPay || 0) + (eosb || 0) + (customColumnsSum || 0);

                  return (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap">{index + 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getDaysBetweenDates(
                          listItem?.employee_contract_start__date,
                          listItem?.employee_contract_end__date
                        ) || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {Math.round(
                          getDaysBetweenDates(
                            listItem?.employee_contract_start__date,
                            listItem?.employee_contract_end__date
                          ) / 30.41
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getBasicSalary(listItem.basicsalary)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTotalSalary(listItem)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{vPay}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{eosb}</td>

                      {/* Custom Column Values - BEFORE TOTAL */}
                      {customColumns.map((column) => (
                        <td key={column.id} className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            step="any"
                            value={customValues[column.id]?.[listItem.journey_id] || ""}
                            onChange={(e) =>
                              handleValueChange(
                                column.id,
                                listItem.journey_id,
                                e.target.value
                              )
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="0.00"
                          />
                        </td>
                      ))}

                      {/* TOTAL - includes custom columns */}
                      <td className="px-6 py-4 whitespace-nowrap font-semibold">
                        {(rowTotal || 0).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-red">
                    <strong>TOTAL</strong>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left text-red">
                    <strong>
                      {data?.contractHistory?.reduce(
                        (total, listItem) =>
                          total +
                          getDaysBetweenDates(
                            listItem?.employee_contract_start__date,
                            listItem?.employee_contract_end__date
                          ),
                        0
                      )}
                    </strong>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left text-red">
                    <strong>
                      {Math.round(
                        data?.contractHistory?.reduce(
                          (total, listItem) =>
                            total +
                            getDaysBetweenDates(
                              listItem?.employee_contract_start__date,
                              listItem?.employee_contract_end__date
                            ) /
                              30.41,
                          0
                        )
                      )}
                    </strong>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left text-red">
                    <strong>
                      {data?.contractHistory?.reduce(
                        (total, listItem) => total + getBasicSalary(listItem.basicsalary),
                        0
                      )}
                    </strong>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left text-red">
                    <strong>
                      {data?.contractHistory?.reduce(
                        (total, listItem) => total + getTotalSalary(listItem),
                        0
                      )}
                    </strong>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left text-red">
                    <strong>
                      {Math.round(
                        data?.contractHistory?.reduce(
                          (total, listItem) =>
                            total +
                            ((((getSalaryForCalculation(listItem) / 30) * 21) /
                              12) *
                              getDaysBetweenDates(
                                listItem?.employee_contract_start__date,
                                listItem?.employee_contract_end__date
                              )) /
                              30.41,
                          0
                        )
                      )}
                    </strong>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left text-red">
                    <strong>
                      {Math.round(
                        data?.contractHistory?.reduce(
                          (total, listItem) =>
                            total +
                            ((((getSalaryForCalculation(listItem) / 30) * 15) /
                              12) *
                              getDaysBetweenDates(
                                listItem?.employee_contract_start__date,
                                listItem?.employee_contract_end__date
                              )) /
                              30.41,
                          0
                        )
                      )}
                    </strong>
                  </td>
                  {/* Custom Column Totals - BEFORE final TOTAL */}
                  {customColumns.map((column) => {
                    const columnTotal = data?.contractHistory?.reduce((total, listItem) => {
                      const value = parseFloat(customValues[column.id]?.[listItem.journey_id] || 0);
                      return total + (isNaN(value) ? 0 : value);
                    }, 0) || 0;

                    return (
                      <td key={column.id} className="px-6 py-4 whitespace-nowrap text-left text-red">
                        <strong>{(columnTotal || 0).toFixed(2)}</strong>
                      </td>
                    );
                  })}
                  {/* Final TOTAL - includes V.PAY + EOSB + all custom columns */}
                  <td className="px-6 py-4 whitespace-nowrap text-left text-red">
                    <strong>
                      {(() => {
                        const vPayTotal = Math.round(
                          data?.contractHistory?.reduce(
                            (total, listItem) =>
                              total +
                              ((((getSalaryForCalculation(listItem) / 30) * 21) /
                                12) *
                                getDaysBetweenDates(
                                  listItem?.employee_contract_start__date,
                                  listItem?.employee_contract_end__date
                                )) /
                                30.41,
                            0
                          )
                        ) || 0;
                        const eosbTotal = Math.round(
                          data?.contractHistory?.reduce(
                            (total, listItem) =>
                              total +
                              ((((getSalaryForCalculation(listItem) / 30) * 15) /
                                12) *
                                getDaysBetweenDates(
                                  listItem?.employee_contract_start__date,
                                  listItem?.employee_contract_end__date
                                )) /
                                30.41,
                            0
                          )
                        ) || 0;
                        const customColumnsTotal = customColumns.reduce((sum, column) => {
                          const columnTotal = data?.contractHistory?.reduce((total, listItem) => {
                            const value = parseFloat(customValues[column.id]?.[listItem.journey_id] || 0);
                            return total + (isNaN(value) ? 0 : value);
                          }, 0) || 0;
                          return sum + (columnTotal || 0);
                        }, 0) || 0;

                        return ((vPayTotal || 0) + (eosbTotal || 0) + (customColumnsTotal || 0)).toFixed(2);
                      })()}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Save Button - Bottom Right */}
          {customColumns.length > 0 && (
            <div className="flex justify-end mt-4">
              <button
                onClick={handleSaveCustomData}
                disabled={isSaving}
                className="px-6 py-2.5 bg-btn-red text-white font-semibold text-xs uppercase rounded-lg hover:bg-btn-red-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {isSaving ? "Saving..." : "Save Custom Columns"}
              </button>
            </div>
          )}
        </>
        </div>
      </div>
    </div>
  );
}
